import React from "react";
import {
    Section,
    SectionTitle,
    SubSection,
    SubTitle,
    Paragraph,
    List,
    Link,
} from "./ContributingUI";
import ApplicationInfo from "./ApplicationInfo";

const Guidelines = () => {
    return (
        <Section id="guidelines">
            <SectionTitle>Guidelines</SectionTitle>
            <Paragraph>
                This page highlights the expectations and requirements for
                applicants for NeST&apos;s Google Summer of Code (GSoC) program,
                if NeST gets accepted in this year&#39;s GSoC Program (2026).
            </Paragraph>

            <SubSection id="project-expectations">
                <SubTitle>Project Expectations</SubTitle>
                <Paragraph>
                    The NeST team is looking for three things from every
                    successful GSoC project:
                </Paragraph>
                <List>
                    <li>
                        Developing code that can be incorporated back into the
                        main codebase, useful to future users.
                    </li>
                    <li>
                        Mentoring contributors that will remain part of the team
                        and contribute to NeST even <strong>after</strong> GSoC
                        ends. We are looking for long-term contributors and
                        maintainers for the project.
                    </li>
                    <li>
                        Providing GSoC contributors with experience and ideas
                        that will be useful to them in their careers.
                    </li>
                </List>
            </SubSection>

            <SubSection id="contributor-expectations">
                <SubTitle>Contributor Expectations</SubTitle>
                <List>
                    <li>
                        At the beginning of the program, you will be required to
                        provide a detailed plan of work covering the duration of
                        GSoC. This should be developed with the help of your
                        mentor.
                    </li>
                    <li>
                        You will be expected to produce mergeable code - either
                        merged completely at the end of the project, partially
                        merged, or with no major roadblocks foreseen to merge
                        shortly after the program ends.
                    </li>
                    <li>
                        You will be expected to follow NeST design guidelines:
                        follow coding style, write tests, and documentation.
                    </li>
                    <li>
                        You are required to post weekly updates on Zulip,
                        covering the progress made or obstacles encountered.
                    </li>
                    <li>
                        Your code will be reviewed by mentors (at least weekly)
                        who will provide guidance and track progress publicly.
                    </li>
                    <li>
                        If you drop the project before GSoC ends, underperform,
                        or do not communicate enough, you will not pass.
                    </li>
                </List>
            </SubSection>

            <SubSection id="expected-background">
                <SubTitle>Expected Background</SubTitle>
                <Paragraph>
                    NeST development requires proficiency in{" "}
                    <strong>Python</strong> and <strong>Git</strong>. Domain
                    knowledge of networking concepts and Linux network
                    namespaces is also expected. We recommend the{" "}
                    <Link
                        href="https://blog.apnic.net/2020/09/18/nest-a-simpleefficient-tool-to-study-congestion-control/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        APNIC article
                    </Link>{" "}
                    and the{" "}
                    <Link
                        href="https://nest.nitk.ac.in/docs/v0.4/user/tutorial.html"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        tutorial
                    </Link>
                    .
                </Paragraph>
            </SubSection>

            <SubSection id="application-development">
                <SubTitle>Application Development</SubTitle>
                <Paragraph>
                    We encourage you to discuss your ideas on{" "}
                    <Link
                        href="https://nitk-nest.zulipchat.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Zulip
                    </Link>{" "}
                    rather than through private messages. This gives the
                    community an opportunity to learn about you.
                </Paragraph>
            </SubSection>

            <SubSection id="selection-process">
                <SubTitle>The Selection Process</SubTitle>
                <Paragraph>Applications will be evaluated based on:</Paragraph>
                <List>
                    <li>
                        <strong>Technical quality:</strong> Is the project
                        feasible and properly scoped?
                    </li>
                    <li>
                        <strong>Project plan:</strong> Are there clear
                        milestones and deliverables?
                    </li>
                    <li>
                        <strong>Mentor availability</strong>
                    </li>
                    <li>
                        <strong>Impact:</strong> Does it bring unique
                        capabilities to NeST?
                    </li>
                    <li>
                        <strong>Community participation</strong>
                    </li>
                </List>
            </SubSection>

            <ApplicationInfo />
        </Section>
    );
};

export default Guidelines;
